/**
 * CallRecorderModal Component
 * Compact consent modal for starting call recordings.
 * WS7: Two-way capture mode with loopback device detection.
 * WS6: Recovery banner for orphaned recordings.
 */

import { useState, useEffect } from 'react'
import { Mic, Monitor, PhoneCall, AlertCircle, CheckCircle2, Info } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useCallRecordingContext, type AudioCaptureMode } from '@/contexts/CallRecordingContext'
import { useAudioDevices } from '@/hooks/useAudioDevices'

interface CallRecorderModalProps {
  isOpen: boolean
  onClose: () => void
  sysId?: string
  table?: string
  recordDescription?: string
  onRecordingStart?: () => void
}

export function CallRecorderModal({
  isOpen,
  onClose,
  sysId,
  table,
  recordDescription,
  onRecordingStart,
}: CallRecorderModalProps) {
  const [captureMode, setCaptureMode] = useState<AudioCaptureMode>('microphone')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [isStarting, setIsStarting] = useState(false)
  const [selectedSpeakerDeviceId, setSelectedSpeakerDeviceId] = useState<string | undefined>(undefined)

  const {
    startRecording,
    isMicrophoneSupported,
    isSystemAudioSupported,
    error: contextError,
    recoverableSession,
    recoverSession,
    dismissRecovery,
  } = useCallRecordingContext()

  const { devices, loopbackDevice, hasLoopback } = useAudioDevices()

  // Auto-select loopback device when detected
  useEffect(() => {
    if (loopbackDevice) {
      setSelectedSpeakerDeviceId(loopbackDevice.deviceId)
    }
  }, [loopbackDevice])

  useEffect(() => {
    if (!isOpen) {
      setCaptureMode('microphone')
      setErrorMessage(null)
      setIsStarting(false)
    }
  }, [isOpen])

  useEffect(() => {
    if (contextError) {
      setErrorMessage(contextError)
      setIsStarting(false)
    }
  }, [contextError])

  const handleStartRecording = async () => {
    try {
      setErrorMessage(null)
      setIsStarting(true)

      const speakerDeviceId = captureMode === 'twoway' ? selectedSpeakerDeviceId : undefined
      await startRecording(captureMode, sysId, table, recordDescription, speakerDeviceId)

      onRecordingStart?.()
      onClose()
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : 'Failed to start recording')
      setIsStarting(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white dark:bg-slate-800 rounded-lg shadow-xl w-full max-w-xs mx-4 p-4">
        <h2 className="text-sm font-semibold dark:text-white mb-1">Record Call</h2>
        {recordDescription && (
          <p className="text-xs text-gray-500 dark:text-slate-400 truncate mb-2">
            {recordDescription}
          </p>
        )}

        <div className="space-y-3">
          {/* Recovery banner (WS6) */}
          {recoverableSession && (
            <div className="flex items-center justify-between p-2 bg-amber-50 dark:bg-amber-900/30 rounded-md text-xs">
              <div className="flex items-center gap-1.5 text-amber-800 dark:text-amber-300">
                <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                <span>Unsaved recording found</span>
              </div>
              <div className="flex gap-1">
                <Button variant="outline" size="sm" className="h-6 text-[10px] px-2" onClick={recoverSession}>
                  Recover
                </Button>
                <Button variant="ghost" size="sm" className="h-6 text-[10px] px-2 text-gray-500" onClick={dismissRecovery}>
                  Discard
                </Button>
              </div>
            </div>
          )}

          <div className="flex gap-2 p-2 bg-blue-50 dark:bg-blue-900/30 rounded-md text-xs text-blue-800 dark:text-blue-300">
            <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
            <p>This call will be recorded and transcribed. Ensure all parties consent.</p>
          </div>

          <div className="space-y-2">
            <label className="text-xs font-medium dark:text-slate-300">Audio Source</label>

            <label
              className={`flex items-center gap-2 p-2 border rounded-md cursor-pointer text-xs dark:text-slate-300 ${
                captureMode === 'microphone' ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' : 'border-gray-200 dark:border-slate-600'
              } ${!isMicrophoneSupported ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <input type="radio" name="captureMode" value="microphone" checked={captureMode === 'microphone'} onChange={() => setCaptureMode('microphone')} disabled={!isMicrophoneSupported} className="shrink-0" />
              <Mic className="h-3.5 w-3.5 shrink-0" />
              <div>
                <span className="font-medium">Microphone</span>
                <span className="text-gray-500 dark:text-slate-400 ml-1">- Your voice only</span>
              </div>
            </label>

            <label
              className={`flex items-center gap-2 p-2 border rounded-md cursor-pointer text-xs dark:text-slate-300 ${
                captureMode === 'twoway' ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' : 'border-gray-200 dark:border-slate-600'
              } ${!isMicrophoneSupported ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <input type="radio" name="captureMode" value="twoway" checked={captureMode === 'twoway'} onChange={() => setCaptureMode('twoway')} disabled={!isMicrophoneSupported} className="shrink-0" />
              <PhoneCall className="h-3.5 w-3.5 shrink-0" />
              <div className="flex items-center gap-1.5">
                <span className="font-medium">Mic + Speaker (Two-Way)</span>
                {hasLoopback && <span className="text-[10px] px-1.5 py-0.5 bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300 rounded-full">Ready</span>}
              </div>
            </label>

            {/* Two-way details */}
            {captureMode === 'twoway' && (
              <div className="pl-7 space-y-2">
                {hasLoopback ? (
                  <div className="flex items-center gap-1.5 text-[10px] text-green-600 dark:text-green-400">
                    <CheckCircle2 className="h-3 w-3" />
                    Loopback device detected — zero dialogs
                  </div>
                ) : (
                  <div className="flex items-start gap-1.5 text-[10px] text-gray-500 dark:text-slate-400">
                    <Info className="h-3 w-3 mt-0.5 shrink-0" />
                    <span>Requires screen share permission. Install <a href="https://vb-audio.com/Cable/" target="_blank" rel="noopener noreferrer" className="text-blue-500 underline">VB-Cable</a> for one-click recording.</span>
                  </div>
                )}
                {/* Device picker override */}
                {devices.length > 1 && (
                  <select
                    value={selectedSpeakerDeviceId || ''}
                    onChange={(e) => setSelectedSpeakerDeviceId(e.target.value || undefined)}
                    className="w-full h-7 text-[10px] border border-gray-300 dark:border-slate-600 rounded px-2 bg-white dark:bg-slate-900 text-gray-700 dark:text-slate-300"
                  >
                    <option value="">Screen share (default)</option>
                    {devices.filter(d => d.deviceId !== 'default').map(d => (
                      <option key={d.deviceId} value={d.deviceId}>
                        {d.label}{d.isLoopback ? ' (loopback)' : ''}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            )}

            <label
              className={`flex items-center gap-2 p-2 border rounded-md cursor-pointer text-xs dark:text-slate-300 ${
                captureMode === 'system' ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' : 'border-gray-200 dark:border-slate-600'
              } ${!isSystemAudioSupported ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <input type="radio" name="captureMode" value="system" checked={captureMode === 'system'} onChange={() => setCaptureMode('system')} disabled={!isSystemAudioSupported} className="shrink-0" />
              <Monitor className="h-3.5 w-3.5 shrink-0" />
              <div>
                <span className="font-medium">System Audio</span>
                <span className="text-gray-500 dark:text-slate-400 ml-1">- Softphone</span>
                {!isSystemAudioSupported && <span className="text-red-500 ml-1">N/A</span>}
              </div>
            </label>
          </div>

          {errorMessage && (
            <div className="flex gap-2 p-2 bg-red-50 dark:bg-red-900/20 rounded-md text-xs text-red-800 dark:text-red-300">
              <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              <p>{errorMessage}</p>
            </div>
          )}

          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={onClose} disabled={isStarting}>Cancel</Button>
            <Button size="sm" onClick={handleStartRecording} disabled={isStarting} className="gap-1.5 bg-blue-600 hover:bg-blue-700 text-white">
              <Mic className="h-3.5 w-3.5" />
              {isStarting ? 'Starting...' : 'Start Recording'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
